/**
 * DER ABGLEICH DER PROJEKTLISTE — nur was sich geaendert hat.
 *
 * ─── Der Anlass ─────────────────────────────────────────────────────────────
 * Ein Mitarbeiter im Feld startete die App im September 234-mal kalt (die
 * Seite stirbt auf seinem Android-Telefon oft), und jeder Start lud die VOLLE
 * Projektliste: 607 Projekte, rund 1,4 MB gzip — rund 330 MB im Monat nur
 * dafuer. Dazu schickte Realtime jedes UPDATE auf `projects` samt `data`-Blob
 * (~40 KB) an jeden verbundenen Client.
 *
 * Seit Nr. 175 (supabase/projekt_signale.sql) haengt die Datenbank fuer JEDE
 * Aenderung an `projects` und `project_services` eine Zeile an
 * `projekt_signale` an: Zeilen-id (Identity), Projekt, `geaendert_am`
 * (Serverzeit des Einfuegens) und `geloescht` (Grabstein). Damit genuegt beim
 * Start die Liste aus dem Geraet (projektListeCache.ts) plus die Projekte, fuer
 * die eine Signalzeile da ist, die die Liste noch nicht kennt.
 *
 * ─── Wann eine Signalzeile BEKANNT ist ─────────────────────────────────────
 * `geaendert_am` ist die Zeit des Einfuegens, nicht des Commits: eine lange
 * Transaktion kann ihr Signal spaeter festschreiben als eine juengere. „Kleiner
 * als der bekannte Stand" ist deshalb KEIN Beweis, dass der Eintrag die
 * Aenderung enthaelt. Stattdessen eine LOGISCHE UHR im Client (das
 * Sichtungsbuch):
 *  - jede Signalzeile bekommt beim ersten Sehen eine Marke n (Abruf, Vorlauf,
 *    Realtime, Nachziehen);
 *  - jeder Abruf von Listenzeilen bekommt beim Start eine Marke m; jeder
 *    Eintrag traegt die Marke des Abrufs, aus dem er stammt — am OBJEKT
 *    (`AbrufMarken`, eine WeakMap): die Marke wandert mit genau dem Eintrag,
 *    der in der Liste steht, und kann nie zu einem anderen Stand passen. Ein
 *    Eintrag ohne Marke ist LOKAL (selbst veraendert, siehe unten);
 *  - eine Signalzeile ist fuer einen Eintrag ABGEDECKT, wenn sie VOR seinem
 *    Abruf gesehen wurde (n < m): sie war festgeschrieben, bevor gelesen wurde.
 *    Eine spaet festgeschriebene Zeile wird erst danach gesehen und loest
 *    deshalb immer ein Nachladen aus.
 * Dieselbe Uhr ordnet konkurrierende Antworten: ein Eintrag aus einem Abruf,
 * der SPAETER begann, ersetzt nie einer aus einem frueheren (Codex-Review
 * 21.10.2026: ein Voll-Load, der vor einem Realtime-Abruf begann, rollte
 * dessen neueren Eintrag zurueck und behielt dessen Stand — fuer immer falsch).
 * Die Uhr ist ein Zaehler, keine Geraetezeit: Uhrspruenge aendern nichts; sie
 * wird mit der Liste gespeichert und laeuft nach einem Neustart weiter.
 *
 * ─── Die weiteren Begriffe ─────────────────────────────────────────────────
 *  - WASSERSTAND der Liste: das juengste `geaendert_am`, das ein ERFOLGREICHER
 *    Abgleich oder Voll-Load gelesen hat. Gelesen wird ab Wasserstand minus
 *    zehn Minuten (Ueberlappung: deckt den Abstand zwischen Einfuegen und
 *    Commit jeder Speichertransaktion).
 *  - LOKAL: ein Eintrag, den dieser Tab selbst veraendert hat (Verschieben,
 *    eigenes Speichern) und den seither kein Abruf bestaetigt hat. Er wird
 *    beim naechsten Abgleich IMMER geladen.
 *
 * Rein und ohne Supabase; die Abrufe stehen in realtimeProjektLader.ts, die
 * Verdrahtung in App.tsx („Projektliste aktuell halten").
 */

import { zeitstempelMikros, type Zeile } from './realtimeProjectSync';

/** Gelesen wird ab Wasserstand minus so viel (Commit-Reihenfolge, siehe oben). */
export const UEBERLAPPUNG_MS = 10 * 60 * 1000;

/** Mehr geaenderte Projekte als das: der Voll-Load ist der kuerzere Weg. */
export const DELTA_HOECHSTENS = 150;

/**
 * So viele Signale holt ein Abruf hoechstens. Kommen so viele, ist der Stand
 * des Geraets zu alt — Voll-Load. (PostgREST kappt auf Supabase ohnehin bei
 * 1.000 Zeilen.)
 */
export const SIGNAL_SEITE = 1000;

/** Sichtungen, die aelter sind als Wasserstand minus so viel, liest kein Abgleich mehr. */
export const SICHTUNG_AUFBEWAHREN_MS = UEBERLAPPUNG_MS + 60 * 1000;

const istObjekt = (x: unknown): x is Record<string, unknown> =>
    typeof x === 'object' && x !== null && !Array.isArray(x);

/**
 * Worauf die Liste hoert:
 *  - `unbekannt`: noch nicht geklaert (Start) — kein Listen-Realtime;
 *  - `an`: `projekt_signale` gibt es — Abgleich, Geraetespeicher, Realtime auf
 *    den Signalen;
 *  - `aus`: die Tabelle fehlt (Nr. 175 nicht gelaufen) — wie bis 10/2026:
 *    Voll-Load, Realtime auf `projects`, kein Geraetespeicher.
 */
export type SignalModus = 'unbekannt' | 'an' | 'aus';

/**
 * Wasserstand und Zeit des letzten VOLL-Loads — als State MIT der Liste
 * festgeschrieben (der Geraetespeicher speichert alles zusammen).
 */
export interface ListenStand {
    wasserstand: string | null;
    /**
     * Beginn des letzten erfolgreichen VOLL-Loads (Geraetezeit). Ein
     * Abgleich zaehlt hier NICHT: ging ein Signal verloren (ein Triggerfehler
     * wird zur Warnung, Nr. 175), heilt nur der Voll-Load den Eintrag.
     */
    vollLoadAmMs: number | null;
}

export const LEERER_LISTENSTAND: ListenStand = { wasserstand: null, vollLoadAmMs: null };

/**
 * Spaetestens so lange nach dem letzten VOLL-Load wird wieder voll geladen —
 * beim Kaltstart (Geraetespeicher) wie im laufenden Betrieb (Nachholen). Ein
 * verlorenes Signal haelt einen Eintrag damit hoechstens einen Tag falsch,
 * auch wenn der Speicher dank anderer Aenderungen staendig frisch geschrieben
 * wird (Codex-Review 21.10.2026, Runde 2).
 */
export const VOLL_LOAD_SPAETESTENS_MS = 24 * 60 * 60 * 1000;

/**
 * Muss jetzt voll geladen werden? Ohne bekannten Voll-Load ja; ebenso, wenn
 * er laenger als einen Tag her ist oder mehr als eine Stunde „in der Zukunft"
 * liegt (Geraeteuhr gesprungen — das Alter ist nicht zu beurteilen).
 */
export const brauchtVollLoad = (vollLoadAmMs: number | null, jetztMs: number): boolean =>
    vollLoadAmMs === null
    || !Number.isFinite(vollLoadAmMs)
    || jetztMs - vollLoadAmMs > VOLL_LOAD_SPAETESTENS_MS
    || vollLoadAmMs - jetztMs > 60 * 60 * 1000;

/**
 * Zeitpunkt des juengsten Voll-Loads nach einem Load, der bei `beginnMs`
 * begann. Normal der spaetere von beiden; liegt der bisherige Wert mehr als
 * eine Stunde nach diesem Beginn (Geraeteuhr zurueckgestellt), gilt der
 * Beginn — sonst bliebe der Wert in der Zukunft stehen, und jeder weitere
 * Abgleich luede voll, bis die Uhr aufgeholt hat (Codex-Review Runde 3, P3).
 */
export const naechsterVollLoadAmMs = (bisherMs: number | null, beginnMs: number): number =>
    bisherMs === null || !Number.isFinite(bisherMs) || bisherMs - beginnMs > 60 * 60 * 1000
        ? beginnMs
        : Math.max(bisherMs, beginnMs);

/**
 * Abruf-Marke je Eintrag-OBJEKT. Ein Eintrag, den dieser Tab selbst ersetzt
 * (Verschieben, eigenes Speichern: neues Objekt), hat keine Marke — er ist
 * LOKAL und wird beim naechsten Abgleich neu geladen.
 */
export type AbrufMarken = WeakMap<object, number>;

/** Die Marke eines Eintrags; `undefined` fuer Lokales und alles ohne Abruf. */
export const markeVon = (marken: AbrufMarken, eintrag: unknown): number | undefined =>
    (typeof eintrag === 'object' && eintrag !== null ? marken.get(eintrag) : undefined);

// ═══════════════════════════════════════════════════════════════════════════
// ZEITEN
// ═══════════════════════════════════════════════════════════════════════════

/** Vergleich zweier Serverzeiten: < 0, 0, > 0 — `null`, wenn eine unlesbar ist. */
export const vergleicheStand = (a: unknown, b: unknown): number | null => {
    const x = zeitstempelMikros(a);
    const y = zeitstempelMikros(b);
    if (x === null || y === null) return null;
    return x - y;
};

/** Die juengere von zwei Serverzeiten; eine unlesbare zaehlt nicht. */
export const juengererStand = (a: string | null | undefined, b: string | null | undefined): string | null => {
    const lesbarA = typeof a === 'string' && zeitstempelMikros(a) !== null;
    const lesbarB = typeof b === 'string' && zeitstempelMikros(b) !== null;
    if (!lesbarA) return lesbarB ? (b as string) : null;
    if (!lesbarB) return a as string;
    return (vergleicheStand(a, b) as number) >= 0 ? (a as string) : (b as string);
};

/**
 * Ab wann gelesen wird: Wasserstand minus Ueberlappung, als ISO-Zeit.
 * Millisekunden genuegen — abgerundet wird FRUEHER gelesen, nie spaeter.
 * `null` ohne lesbaren Wasserstand.
 */
export const lesenAb = (wasserstand: string | null | undefined, ueberlappungMs = UEBERLAPPUNG_MS): string | null => {
    const mikros = zeitstempelMikros(wasserstand);
    if (mikros === null) return null;
    return new Date(Math.floor(mikros / 1000) - ueberlappungMs).toISOString();
};

// ═══════════════════════════════════════════════════════════════════════════
// SIGNALE LESEN — aus PostgREST und aus Realtime
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Signalzeile. */
export interface Signal {
    /** `projekt_signale.id` (Identity). */
    zeile: number;
    /** Das Projekt. */
    id: string;
    /** `geaendert_am` — Serverzeit des Einfuegens. */
    stand: string;
    geloescht: boolean;
}

const alsZeilenId = (wert: unknown): number | null => {
    if (typeof wert === 'number' && Number.isSafeInteger(wert) && wert > 0) return wert;
    if (typeof wert === 'string' && /^\d{1,15}$/.test(wert)) return Number(wert);
    return null;
};

/** Eine Zeile aus `projekt_signale` — oder `null`, wenn sie dafuer nicht taugt. */
export const leseSignalZeile = (zeile: unknown): Signal | null => {
    if (!istObjekt(zeile)) return null;
    const nr = alsZeilenId(zeile.id);
    const id = zeile.project_id;
    const stand = zeile.geaendert_am;
    if (nr === null || typeof id !== 'string' || !id || typeof stand !== 'string' || zeitstempelMikros(stand) === null) return null;
    return { zeile: nr, id, stand, geloescht: zeile.geloescht === true };
};

export type SignalAenderung =
    | { art: 'loeschen'; signal: Signal }
    | { art: 'merken'; signal: Signal }
    /** Ausgeduennte Signalzeile (DELETE) — kein Projekt betroffen. */
    | { art: 'ignorieren' }
    | { art: 'unbekannt' };

/** Liest ein `postgres_changes`-Ereignis auf `projekt_signale`. */
export const leseSignalAenderung = (payload: unknown): SignalAenderung => {
    if (!istObjekt(payload)) return { art: 'unbekannt' };
    if (payload.eventType === 'DELETE') return { art: 'ignorieren' };
    const signal = leseSignalZeile(payload.new);
    if (!signal) return { art: 'unbekannt' };
    return signal.geloescht ? { art: 'loeschen', signal } : { art: 'merken', signal };
};

/** Die Vormerkung des Sammlers fuer ein Signal: Projekt und Signalzeile, kein Blob. */
export const signalVormerkung = (signal: Signal): Zeile => ({ id: signal.id, signal_zeile: signal.zeile, geaendert_am: signal.stand });

/** Die Signalzeile, die eine Vormerkung traegt — `null` bei anderen Vormerkungen. */
export const zeileDerVormerkung = (neu: unknown): number | null =>
    (istObjekt(neu) ? alsZeilenId(neu.signal_zeile) : null);

// ═══════════════════════════════════════════════════════════════════════════
// DAS SICHTUNGSBUCH — die logische Uhr
// ═══════════════════════════════════════════════════════════════════════════

export interface Sichtung {
    /** Marke beim ersten Sehen. */
    n: number;
    /** `geaendert_am` der Zeile (zum Ausduennen des Buchs). */
    t: string;
    /** Das Projekt. */
    p: string;
}

/** Veraenderlich, lebt in einem Ref (App) und wird mit der Liste gespeichert. */
export interface Sichtungsbuch {
    zaehler: number;
    zeilen: Map<number, Sichtung>;
}

export const neuesSichtungsbuch = (zaehler = 0, zeilen: Iterable<[number, Sichtung]> = []): Sichtungsbuch =>
    ({ zaehler, zeilen: new Map(zeilen) });

/** Die naechste Marke — fuer den Start eines Abrufs. */
export const naechsteMarke = (buch: Sichtungsbuch): number => {
    buch.zaehler += 1;
    return buch.zaehler;
};

/** Eine Signalzeile sehen: beim ERSTEN Mal bekommt sie eine Marke; danach bleibt die alte. */
export const sichte = (buch: Sichtungsbuch, signal: Signal): Sichtung => {
    const bekannt = buch.zeilen.get(signal.zeile);
    if (bekannt) return bekannt;
    const neu: Sichtung = { n: naechsteMarke(buch), t: signal.stand, p: signal.id };
    buch.zeilen.set(signal.zeile, neu);
    return neu;
};

/** Ist die Signalzeile fuer einen Eintrag mit dieser Abruf-Marke abgedeckt (vor dem Abruf gesehen)? */
export const istAbgedeckt = (sichtung: Sichtung | undefined, marke: number | undefined): boolean =>
    sichtung !== undefined && marke !== undefined && sichtung.n < marke;

/**
 * Das Buch kuerzen: Zeilen, die aelter sind als Wasserstand minus
 * Ueberlappung (und eine Minute), liest kein Abgleich mehr, und Realtime
 * liefert eine Zeile nur einmal. Ohne Wasserstand bleibt alles.
 */
export const lichteSichtungsbuch = (buch: Sichtungsbuch, wasserstand: string | null): void => {
    const ab = zeitstempelMikros(lesenAb(wasserstand, SICHTUNG_AUFBEWAHREN_MS));
    if (ab === null) return;
    for (const [nr, s] of buch.zeilen) {
        const t = zeitstempelMikros(s.t);
        if (t !== null && t < ab) buch.zeilen.delete(nr);
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// DER PLAN
// ═══════════════════════════════════════════════════════════════════════════

export interface DeltaEingabe {
    /** Kennungen der Liste, wie sie jetzt steht. */
    liste: Iterable<string>;
    /** Abruf-Marke des Eintrags; `undefined` = LOKAL (immer laden). */
    marke: (id: string) => number | undefined;
    /** Die Signalzeilen ab Wasserstand minus Ueberlappung (schon gesichtet). */
    signale: readonly Signal[];
    /** Die Sichtung einer Signalzeile. */
    sichtung: (zeile: number) => Sichtung | undefined;
    /** Was der Server dem Leser JETZT zeigt; `null` = nicht gelesen. */
    sichtbar: ReadonlySet<string> | null;
    hoechstens?: number;
}

export interface DeltaPlan {
    /** Zu viel geaendert — voll laden. */
    voll: boolean;
    /** Zu laden, in Listenform. */
    zuLaden: string[];
    /** Aus der Liste zu nehmen (Grabstein, nicht mehr sichtbar). */
    entfernen: string[];
    /** Juengstes gelesenes `geaendert_am` — der Wasserstand NACH diesem Abgleich (oder `null`). */
    wasserstand: string | null;
}

/**
 * Was ein Abgleich laden und entfernen muss.
 *
 *  - Grabstein → entfernen.
 *  - Signal eines Projekts, das der Server dem Leser nicht (mehr) zeigt →
 *    entfernen, nicht laden.
 *  - ALLE Signalzeilen des Projekts fuer seinen Eintrag abgedeckt → nichts.
 *  - sonst → laden (neu oder geaendert).
 *  - sichtbar, aber nicht in der Liste (Freigabe, Vertretung, Uebertragung an
 *    mich — Rechte koennen sich ohne Signal aendern) → laden.
 *  - in der Liste, aber nicht mehr sichtbar → entfernen.
 *  - LOKAL (ohne Marke) und sichtbar → laden.
 */
export const planeDelta = (eingabe: DeltaEingabe): DeltaPlan => {
    const hoechstens = eingabe.hoechstens ?? DELTA_HOECHSTENS;
    const inListe = new Set(eingabe.liste);
    const sichtbar = eingabe.sichtbar;
    const zuLaden = new Set<string>();
    const entfernen = new Set<string>();
    let wasserstand: string | null = null;

    const jeProjekt = new Map<string, Signal[]>();
    for (const s of eingabe.signale) {
        wasserstand = juengererStand(wasserstand, s.stand);
        const liste = jeProjekt.get(s.id);
        if (liste) liste.push(s);
        else jeProjekt.set(s.id, [s]);
    }

    for (const [id, signale] of jeProjekt) {
        if (signale.some((s) => s.geloescht) || (sichtbar && !sichtbar.has(id))) {
            entfernen.add(id);
            continue;
        }
        const marke = eingabe.marke(id);
        const bekannt = inListe.has(id)
            && signale.every((s) => istAbgedeckt(eingabe.sichtung(s.zeile), marke));
        if (!bekannt) zuLaden.add(id);
    }

    if (sichtbar) {
        for (const id of sichtbar) {
            if (!inListe.has(id) && !entfernen.has(id)) zuLaden.add(id);
        }
    }
    for (const id of inListe) {
        if (sichtbar && !sichtbar.has(id)) { entfernen.add(id); continue; }
        if (eingabe.marke(id) === undefined && !entfernen.has(id)) zuLaden.add(id);
    }

    return { voll: zuLaden.size > hoechstens, zuLaden: [...zuLaden], entfernen: [...entfernen], wasserstand };
};

// ═══════════════════════════════════════════════════════════════════════════
// ERGEBNISSE IN DIE LISTE — Abgleich, Realtime, Voll-Load
// ═══════════════════════════════════════════════════════════════════════════

type Eintrag = { id?: unknown; updated_at?: unknown };

/** Reihenfolge wie der Voll-Load: juengste Aenderung zuerst, dann nach id. */
const listenFolge = (a: Eintrag, b: Eintrag): number => {
    const zeit = (x: Eintrag): number => {
        const t = Date.parse(typeof x.updated_at === 'string' ? x.updated_at : '');
        return Number.isNaN(t) ? 0 : t;
    };
    return zeit(b) - zeit(a) || String(a.id).localeCompare(String(b.id));
};

export interface ZusammenfuehrenKontext {
    /** Abruf-Marke eines Eintrag-Objekts (AbrufMarken). */
    markeVon: (eintrag: unknown) => number | undefined;
    /** Marke des Abrufs, aus dem die neuen Eintraege stammen. */
    ladeMarke: number;
    /**
     * Marke, ab der ein Entfernen gilt: ein Eintrag aus einem Abruf, der
     * DANACH begann, bleibt (er ist juenger als das Wissen „weg").
     */
    entfernenMarke: number;
    /**
     * Start-Marke des juengsten erfolgreichen Abgleichs bzw. Voll-Loads ueber
     * die GANZE Liste. Begann er nach `ladeMarke` und kennt ein Projekt nicht,
     * ist es inzwischen weg — eine aeltere Antwort setzt es nicht wieder ein.
     */
    letzterGanzerAbgleich: number | null;
    /** Wann eine Kennung per Realtime NEU in die Liste kam (Geraetezeit). */
    eingefuegtSeit: (id: string) => number | undefined;
    /** Wann eine Kennung per Realtime geloescht wurde (Geraetezeit). */
    geloeschtSeit: (id: string) => number | undefined;
    /** Geraetezeit des Abrufbeginns (fuer die beiden Merker darueber). */
    abfrageBeginnMs: number;
}

/**
 * Fuehrt frisch geladene Eintraege (Abgleich oder Realtime) in die Liste:
 *
 *  - ersetzen nur, wenn der vorhandene Eintrag NICHT aus einem spaeter
 *    begonnenen Abruf stammt (seine Marke > `ladeMarke` → bleibt) —
 *    ein lokal veraenderter (ohne Marke) wird ersetzt;
 *  - neu einfuegen nur, wenn kein spaeter begonnener ganzer Abgleich es schon
 *    nicht mehr kannte, und nicht, wenn es nach dem Abrufbeginn per Realtime
 *    geloescht wurde;
 *  - entfernen nur, was nicht aus einem spaeter begonnenen Abruf stammt und
 *    nicht nach dem Abrufbeginn per Realtime neu hereinkam;
 *  - ohne jede Aenderung kommt `prev` selbst zurueck (kein neuer Render).
 *
 * Die Marke der neuen Eintraege muss der Aufrufer VORHER an ihre Objekte
 * gehaengt haben (AbrufMarken) — ein nicht uebernommenes Objekt gelangt nie in
 * die Liste, seine Marke stoert dort nicht.
 */
export const fuehreZusammen = <T extends Eintrag>(
    prev: T[],
    geladen: readonly T[],
    entfernen: ReadonlySet<string>,
    k: ZusammenfuehrenKontext,
): T[] => {
    const juengerGeladen = (eintrag: T, marke: number): boolean => (k.markeVon(eintrag) ?? -Infinity) > marke;
    const neu = new Map<string, T>();
    for (const e of geladen) {
        if (typeof e.id !== 'string') continue;
        const geloescht = k.geloeschtSeit(e.id);
        if (geloescht !== undefined && geloescht >= k.abfrageBeginnMs) continue;
        neu.set(e.id, e);
    }
    let geaendert = false;
    const liste: T[] = [];
    for (const p of prev) {
        const id = typeof p.id === 'string' ? p.id : null;
        if (id === null) { liste.push(p); continue; }
        const ersatz = neu.get(id);
        if (ersatz) {
            neu.delete(id);
            if (juengerGeladen(p, k.ladeMarke)) { liste.push(p); continue; }
            if (ersatz !== p) geaendert = true;
            liste.push(ersatz);
            continue;
        }
        if (entfernen.has(id) && !juengerGeladen(p, k.entfernenMarke)) {
            const seit = k.eingefuegtSeit(id);
            if (seit === undefined || seit <= k.abfrageBeginnMs) {
                geaendert = true;
                continue;
            }
        }
        liste.push(p);
    }
    for (const e of neu.values()) {
        if (k.letzterGanzerAbgleich !== null && k.letzterGanzerAbgleich > k.ladeMarke) continue;
        liste.push(e);
        geaendert = true;
    }
    if (!geaendert) return prev;
    return liste.sort(listenFolge);
};

/**
 * Der Voll-Load in die Liste: seine Eintraege ersetzen alles — ausser
 * Eintraegen aus einem Abruf, der NACH dem Voll-Load begann. Die wissen mehr
 * als er:
 *  - steht das Projekt auch im Voll-Load, bleibt der juengere Eintrag
 *    (Codex-Review 21.10.2026: ein Voll-Load, der V1 las, ueberschrieb das per
 *    Realtime geholte V2, und dessen Marke blieb stehen — fuer immer falsch);
 *  - fehlt es im Voll-Load, bleibt es trotzdem (Runde 2: ein Projekt, das nach
 *    dem Beginn des Voll-Loads angelegt und von einem Abgleich schon
 *    eingefuegt wurde, nahm die aeltere Antwort wieder heraus) — ausser es ist
 *    inzwischen geloescht (Grabstein, `istGeloescht`).
 * Ohne Marke des Loads (kein Signalmodus) ersetzt er alles wie bisher.
 * Reihenfolge: die nur noch vorhandenen vorne (wie neu eingefuegte), dann die
 * Eintraege des Loads.
 */
export const behalteJuengere = <T extends Eintrag>(
    frisch: readonly T[],
    prev: readonly T[],
    markeVon: (eintrag: unknown) => number | undefined,
    vollMarke: number | null,
    istGeloescht: (id: string) => boolean = () => false,
): T[] => {
    if (vollMarke === null) return [...frisch];
    const juenger = (e: T): boolean => (markeVon(e) ?? -Infinity) > vollMarke;
    const vorhanden = new Map<string, T>();
    for (const p of prev) if (typeof p.id === 'string') vorhanden.set(p.id, p);
    const imLoad = new Set<string>();
    const ergebnis = frisch.map((e) => {
        if (typeof e.id !== 'string') return e;
        imLoad.add(e.id);
        const alt = vorhanden.get(e.id);
        return alt && juenger(alt) ? alt : e;
    });
    const nurVorhanden: T[] = [];
    for (const p of prev) {
        if (typeof p.id !== 'string' || imLoad.has(p.id)) continue;
        if (juenger(p) && !istGeloescht(p.id)) nurVorhanden.push(p);
    }
    return nurVorhanden.length > 0 ? [...nurVorhanden, ...ergebnis] : ergebnis;
};
